import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LS_LAYOUT = 'dashboardLayout';
const LS_WIDTHS = 'dashboardPaneWidths';
const LS_GRID = 'dashboardLayoutGrid';

/**
 * Centralized state + persistence for the dashboard's layout.
 *
 * Supports two shapes:
 *   - `layout` (flat array) — legacy single-row layout (e.g. `['bible','votd']`).
 *   - `layoutGrid` (2D array of rows) — multi-row layout like
 *     `[['bible'], ['notes','highlights']]`. Each inner array is one row.
 *
 * Persistence is hybrid: localStorage for instant restore on next mount,
 * `/api/user/preferences` for cross-device sync. localStorage is treated as
 * an instant cache; the server's value wins after first round-trip.
 *
 * The hook ALWAYS keeps `layout` and `layoutGrid` in sync — flattening the
 * grid into `layout` on every write so legacy renderers don't break.
 */
export const useDashboardPreferences = ({ language } = { language: 'en' }) => {
  const [layout, setLayout] = useState(['bible']);
  const [layoutGrid, setLayoutGrid] = useState(null);
  const [paneWidths, setPaneWidths] = useState({});

  // Initial load — localStorage first for instant paint, then server merge.
  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      try {
        const lsLayout = window.localStorage.getItem(LS_LAYOUT);
        const lsWidths = window.localStorage.getItem(LS_WIDTHS);
        const lsGrid = window.localStorage.getItem(LS_GRID);

        if (lsLayout) {
          const parsed = JSON.parse(lsLayout);
          if (Array.isArray(parsed) && parsed.length > 0) setLayout(parsed);
        }
        if (lsGrid) {
          try {
            const parsedGrid = JSON.parse(lsGrid);
            if (Array.isArray(parsedGrid) && parsedGrid.every(Array.isArray)) {
              setLayoutGrid(parsedGrid);
            }
          } catch { /* ignore */ }
        }
        if (lsWidths) {
          try { setPaneWidths(JSON.parse(lsWidths)); } catch { /* ignore */ }
        }

        const r = await axios.get(`${BACKEND_URL}/api/user/preferences`, { withCredentials: true });
        if (cancelled) return;
        if (Array.isArray(r.data?.layout) && r.data.layout.length > 0) {
          setLayout(r.data.layout);
          window.localStorage.setItem(LS_LAYOUT, JSON.stringify(r.data.layout));
        } else if (!lsLayout) {
          setLayout(['bible']);
          window.localStorage.setItem(LS_LAYOUT, JSON.stringify(['bible']));
        }
        if (Array.isArray(r.data?.layoutGrid)) {
          setLayoutGrid(r.data.layoutGrid);
          window.localStorage.setItem(LS_GRID, JSON.stringify(r.data.layoutGrid));
        }
        if (r.data?.paneWidths) {
          setPaneWidths(r.data.paneWidths);
          window.localStorage.setItem(LS_WIDTHS, JSON.stringify(r.data.paneWidths));
        }
      } catch {
        // Stay with whatever localStorage gave us; if nothing, default to bible.
        if (!window.localStorage.getItem(LS_LAYOUT)) {
          setLayout(['bible']);
          window.localStorage.setItem(LS_LAYOUT, JSON.stringify(['bible']));
        }
      }
    };
    init();
    return () => { cancelled = true; };
  }, []);

  const persistRemote = useCallback(async (body) => {
    try {
      await axios.put(`${BACKEND_URL}/api/user/preferences`, body, { withCredentials: true });
    } catch (e) {
      console.error('Error saving layout:', e);
      toast.error(language === 'es' ? 'Error al guardar diseño' : 'Error saving layout');
    }
  }, [language]);

  const saveLayout = useCallback(async (newLayout, showToast = true) => {
    setLayout(newLayout);
    setLayoutGrid(null); // explicit flat layout — clear any multi-row grid
    window.localStorage.setItem(LS_LAYOUT, JSON.stringify(newLayout));
    window.localStorage.removeItem(LS_GRID);
    await persistRemote({ layout: newLayout, layoutGrid: null, paneWidths });
    if (showToast) toast.success(language === 'es' ? 'Diseño guardado' : 'Layout saved');
  }, [paneWidths, persistRemote, language]);

  const saveLayoutGrid = useCallback(async (grid, showToast = true) => {
    const flat = grid.flat();
    setLayoutGrid(grid);
    setLayout(flat);
    window.localStorage.setItem(LS_GRID, JSON.stringify(grid));
    window.localStorage.setItem(LS_LAYOUT, JSON.stringify(flat));
    await persistRemote({ layout: flat, layoutGrid: grid, paneWidths });
    if (showToast) toast.success(language === 'es' ? 'Diseño guardado' : 'Layout saved');
  }, [paneWidths, persistRemote, language]);

  const handlePaneResize = useCallback(async (paneId, newWidth) => {
    const updated = { ...paneWidths, [paneId]: newWidth };
    setPaneWidths(updated);
    window.localStorage.setItem(LS_WIDTHS, JSON.stringify(updated));
    await persistRemote({ layout, layoutGrid, paneWidths: updated });
  }, [paneWidths, layout, layoutGrid, persistRemote]);

  const removePane = useCallback((paneId) => {
    if (layoutGrid) {
      const next = layoutGrid.map((row) => row.filter((p) => p !== paneId)).filter((row) => row.length);
      if (next.flat().length === 0) {
        toast.error(language === 'es' ? 'Debe tener al menos un panel' : 'Must have at least one pane');
        return;
      }
      saveLayoutGrid(next);
    } else {
      const next = layout.filter((p) => p !== paneId);
      if (next.length === 0) {
        toast.error(language === 'es' ? 'Debe tener al menos un panel' : 'Must have at least one pane');
        return;
      }
      saveLayout(next);
    }
    toast.success(language === 'es' ? 'Panel eliminado' : 'Pane removed');
  }, [layout, layoutGrid, saveLayout, saveLayoutGrid, language]);

  return {
    layout,
    layoutGrid,
    paneWidths,
    saveLayout,
    saveLayoutGrid,
    handlePaneResize,
    removePane,
  };
};
